import { useState } from 'react';
import { ChevronRight, Plus, GripVertical, Trash2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PageEntry, PageTreeNode } from '@/lib/stele';
import { buildPageTree, getPathDepth, MAX_PAGE_DEPTH, pageSlugFromDTag } from '@/lib/stele';
import { CreatePageDialog } from './CreatePageDialog';

interface PageTreeSidebarProps {
  pages: PageEntry[];
  activePage?: string;
  onSelectPage: (dTag: string) => void;
  onCreatePage: (title: string, slug: string, parentPath?: string) => Promise<void>;
  onDeletePage: (dTag: string) => void;
  onReorderPages: (pages: PageEntry[]) => void;
  pageTitles: Map<string, string>;
}

export function PageTreeSidebar({
  pages, activePage, onSelectPage, onCreatePage, onDeletePage, onReorderPages, pageTitles,
}: PageTreeSidebarProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createParentPath, setCreateParentPath] = useState<string | undefined>();
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const tree = buildPageTree(pages);

  const toggleExpand = (path: string) =>
    setExpandedPaths(prev => {
      const s = new Set(prev);
      s.has(path) ? s.delete(path) : s.add(path);
      return s;
    });

  const handleDrop = (targetDTag: string) => {
    if (!draggedItem || draggedItem === targetDTag) {
      setDraggedItem(null); setDragOverItem(null); return;
    }
    const newPages = [...pages];
    const dragIdx  = newPages.findIndex(p => p.dTag === draggedItem);
    const dropIdx  = newPages.findIndex(p => p.dTag === targetDTag);
    if (dragIdx >= 0 && dropIdx >= 0) {
      const [moved] = newPages.splice(dragIdx, 1);
      newPages.splice(dropIdx, 0, moved);
      onReorderPages(newPages);
    }
    setDraggedItem(null); setDragOverItem(null);
  };

  const renderNode = (node: PageTreeNode, depth = 0) => {
    const isActive     = node.entry.dTag === activePage;
    const hasChildren  = node.children.length > 0;
    const isExpanded   = expandedPaths.has(node.entry.path);
    const isDragOver   = dragOverItem === node.entry.dTag;
    const title        = pageTitles.get(node.entry.dTag) ?? pageSlugFromDTag(node.entry.dTag) ?? 'Untitled';
    const canAddChild  = getPathDepth(node.entry.path) < MAX_PAGE_DEPTH;

    return (
      <div key={node.entry.dTag}>
        <div
          className={cn(
            'group flex items-center gap-0.5 py-[5px] pr-1 text-[13px] cursor-pointer transition-colors select-none',
            isActive
              ? 'text-foreground bg-accent'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
            isDragOver && 'border-t border-foreground/30',
          )}
          style={{ paddingLeft: `${depth * 14 + 8}px` }}
          onClick={() => onSelectPage(node.entry.dTag)}
          draggable
          onDragStart={() => setDraggedItem(node.entry.dTag)}
          onDragOver={(e) => { e.preventDefault(); setDragOverItem(node.entry.dTag); }}
          onDrop={() => handleDrop(node.entry.dTag)}
          onDragEnd={() => { setDraggedItem(null); setDragOverItem(null); }}
        >
          <GripVertical className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-25 cursor-grab" />

          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.entry.path); }}
              className="shrink-0 p-0.5"
            >
              {isExpanded
                ? <ChevronDown  className="h-3 w-3" />
                : <ChevronRight className="h-3 w-3" />
              }
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}

          <span className="truncate flex-1 ml-0.5 leading-snug">{title}</span>

          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
            {canAddChild && (
              <button
                onClick={(e) => { e.stopPropagation(); setCreateParentPath(node.entry.path); setCreateDialogOpen(true); }}
                className="p-1 hover:text-foreground"
                title="Add child page"
              >
                <Plus className="h-2.5 w-2.5" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDeletePage(node.entry.dTag); }}
              className="p-1 hover:text-destructive"
              title="Delete page"
            >
              <Trash2 className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>{node.children.map(child => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <span className="text-[10px] font-mono tracking-[0.18em] text-muted-foreground/60 uppercase">
          Pages
        </span>
        <button
          onClick={() => { setCreateParentPath(undefined); setCreateDialogOpen(true); }}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-1">
        {tree.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-[11px] font-mono text-muted-foreground/40 uppercase tracking-wider mb-3">
              No pages
            </p>
            <button
              onClick={() => { setCreateParentPath(undefined); setCreateDialogOpen(true); }}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors font-mono"
            >
              + New page
            </button>
          </div>
        ) : (
          tree.map(node => renderNode(node))
        )}
      </div>

      <CreatePageDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={(title, slug) => onCreatePage(title, slug, createParentPath)}
        parentPath={createParentPath}
      />
    </div>
  );
}
